import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Spin } from "antd";
import { Modal } from "react-bootstrap";
import { jwtDecode } from "jwt-decode";

import * as recruitmentApi from "../../../../core/data/redux/services/recruitmentApi";
import GuestAuthModal from "../components/common/GuestAuthModal";
import { PublicJob, PublicCareerSettings, hexAlpha } from "../types";
import CareerNavbar from "../components/common/CareerNavbar";
import HeroBanner from "../components/common/HeroBanner";
import JobFilters from "../components/job/JobFilters";
import JobCard from "../components/job/JobCard";
import CareerFooter from "../components/common/CareerFooter";
import { notify } from "../../../../services/toast.config";

const getEmailFromToken = (token: string | null): string | null => {
  if (!token) return null;
  try {
    const decoded: any = jwtDecode(token);
    return decoded?.user_type === "guest" ? decoded?.email : null;
  } catch {
    return null;
  }
};

const CareerPage: React.FC = () => {
  const { career_page_uuid } = useParams<{ career_page_uuid: string }>();

  const [jobs, setJobs] = useState<PublicJob[]>([]);
  const [settings, setSettings] = useState<PublicCareerSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [type, setType] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [guestToken, setGuestToken] = useState<string | null>(
    localStorage.getItem("guest_token")
  );

  
  const [alertModal, setAlertModal] = useState(false);
  const [alertEmail, setAlertEmail] = useState("");
  const [alertFrequency, setAlertFrequency] = useState<"daily" | "weekly">("daily");
  const [alertLoading, setAlertLoading] = useState(false);
  const [alertSubscribed, setAlertSubscribed] = useState<boolean>(false);
  const [alertStatusLoading, setAlertStatusLoading] = useState(false);


  
  const handleOpenAlertModal = async () => {
    const emailFromToken = getEmailFromToken(guestToken);
    if (emailFromToken) {
      setAlertEmail(emailFromToken);
      if (career_page_uuid) {
        setAlertStatusLoading(true);
        try {
          const status = await recruitmentApi.getJobAlertStatus(emailFromToken, career_page_uuid);
          setAlertSubscribed(status.is_subscribed);
          if (status.is_subscribed && status.frequency) {
            setAlertFrequency(status.frequency);
          }
        } catch {
          setAlertSubscribed(false);
        } finally {
          setAlertStatusLoading(false);
        }
      }
    } else {
      setAlertSubscribed(false);
    }
    setAlertModal(true);
  };

  
  const handleAuthSuccess = (token: string) => {
    setGuestToken(token);
    const emailFromToken = getEmailFromToken(token);
    if (emailFromToken) setAlertEmail(emailFromToken);
  };

  const fetchCareerPage = useCallback(async () => {
    if (!career_page_uuid) { setError(true); setLoading(false); return; }
    setLoading(true);
    setError(false);
    try {
      const data = await recruitmentApi.getPublicCareerPageByUuid(career_page_uuid, {
        search: searchQuery || undefined,
        employment_type: type || undefined,
      });
      setJobs(data?.jobs || []);
      setSettings(data?.settings || null);
    } catch (err: any) {
      setError(true);
      notify.error(err?.data?.error?.message || err?.data?.message || err?.message || "Failed to load career page!");
    } finally {
      setLoading(false);
    }
  }, [career_page_uuid, searchQuery, type]);

  useEffect(() => { fetchCareerPage(); }, [fetchCareerPage]);

  const handleAlertSubscribe = async () => {
    if (!alertEmail.trim()) { notify.warn("Please enter your email."); return; }
    if (!career_page_uuid) { notify.error("Career page not found."); return; }

    setAlertLoading(true);
    try {
      await recruitmentApi.subscribeToJobAlerts(
        alertEmail.trim(),
        career_page_uuid,
        alertFrequency,
      );
      notify.success("You're subscribed! We'll notify you of new openings.");
      setAlertModal(false);
      setAlertEmail("");
    } catch (err: any) {
      notify.error(err?.data?.error?.message || err?.data?.message || err?.message || "Failed to subscribe");
    } finally {
      setAlertLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("guest_token");
    setGuestToken(null);
    notify.success("Logged out successfully");
  };

  if (loading && !settings) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", background: "#f8f9fa" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || (!loading && !settings)) {
    return (
      <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100vh", background: "#f8f9fa" }}>
        <i className="ti ti-alert-circle text-danger mb-3" style={{ fontSize: 64 }} />
        <h2 className="fw-bold">Career Page Not Found</h2>
        <p className="text-muted">This career page does not exist or is no longer available.</p>
      </div>
    );
  }

  const primaryColor = settings?.primary_color || "#4f46e5";
  const loggedInEmail = getEmailFromToken(guestToken);

  return (
    <div style={{ background: "#f4f5f7", minHeight: "100vh", fontFamily: "'Inter', sans-serif", display: "flex", flexDirection: "column" }}>

      <CareerNavbar
        settings={settings}
        guestToken={guestToken}
        onLoginClick={() => setShowAuthModal(true)}
        onLogoutClick={handleLogout}
      />

      <HeroBanner
        settings={settings}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        setSearchQuery={setSearchQuery}
      />

      <JobFilters
        primaryColor={primaryColor}
        type={type}
        setType={setType}
        searchQuery={searchQuery}
        jobsLength={jobs.length}
        viewMode={viewMode}
        setViewMode={setViewMode}
        onSubscribeAlerts={handleOpenAlertModal}
      />

      
      <div style={{ width: "100%", margin: "0 auto", padding: "28px 40px", flex: 1 }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <Spin size="large" />
            <p style={{ marginTop: 16, color: "#888", fontSize: 14 }}>Fetching opportunities...</p>
          </div>
        ) : jobs.length > 0 ? (
          <div style={{
            display: viewMode === "grid" ? "grid" : "flex",
            gridTemplateColumns: viewMode === "grid" ? "repeat(auto-fill, minmax(340px, 1fr))" : undefined,
            flexDirection: viewMode === "list" ? "column" : undefined,
            gap: 16,
          }}>
            {jobs.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                career_page_uuid={career_page_uuid!}
                viewMode={viewMode}
                primaryColor={primaryColor}
              />
            ))}
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <div style={{
              width: 72, height: 72, borderRadius: "50%",
              background: "#f4f5f7", display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 20px",
            }}>
              <i className="ti ti-briefcase-off" style={{ fontSize: 32, color: "#bbb" }} />
            </div>
            <h5 style={{ fontWeight: 700, color: "#333", marginBottom: 8 }}>No openings match your criteria</h5>
            <p style={{ color: "#888", fontSize: 14 }}>
              Try broadening your search, or{" "}
              <span
                style={{ color: primaryColor, cursor: "pointer", fontWeight: 600 }}
                onClick={handleOpenAlertModal}
              >
                subscribe to job alerts
              </span>
              {" "}to be notified when new roles open up.
            </p>
          </div>
        )}
      </div>

      <CareerFooter settings={settings} />

      
      <Modal show={alertModal} onHide={() => setAlertModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: "1px solid #f0f0f0" }}>
          <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
            <i className="ti ti-bell-ringing me-2" style={{ color: primaryColor }} />
            Subscribe to Job Alerts
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: "20px 24px" }}>
          {alertStatusLoading ? (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <Spin size="small" />
              <p style={{ marginTop: 10, fontSize: 13, color: "#888" }}>Checking your subscription...</p>
            </div>
          ) : alertSubscribed ? (
            
            <div>
              <div style={{
                display: "flex", alignItems: "flex-start", gap: 12,
                background: "#f6ffed", border: "1px solid #b7eb8f",
                borderRadius: 10, padding: "14px 16px", marginBottom: 16,
              }}>
                <i className="ti ti-circle-check" style={{ color: "#52c41a", fontSize: 20, marginTop: 1, flexShrink: 0 }} />
                <div>
                  <p style={{ fontSize: 13, fontWeight: 600, color: "#333", margin: "0 0 2px" }}>
                    You're already subscribed!
                  </p>
                  <p style={{ fontSize: 12, color: "#666", margin: 0 }}>
                    We're sending <strong>{alertFrequency}</strong> alerts to <strong>{alertEmail}</strong>.
                  </p>
                </div>
              </div>

              
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#444", display: "block", marginBottom: 8 }}>
                  Change frequency
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  {(["daily", "weekly"] as const).map((freq) => (
                    <button
                      key={freq}
                      onClick={() => setAlertFrequency(freq)}
                      style={{
                        flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 13,
                        fontWeight: alertFrequency === freq ? 600 : 400,
                        border: `1.5px solid ${alertFrequency === freq ? primaryColor : "#e2e8f0"}`,
                        background: alertFrequency === freq ? hexAlpha(primaryColor, 0.08) : "#fff",
                        color: alertFrequency === freq ? primaryColor : "#888",
                        cursor: "pointer", transition: "all 0.15s",
                      }}
                    >
                      {freq === "daily" ? "📅 Daily" : "📆 Weekly"}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            
            <div>
              <p style={{ fontSize: 13, color: "#666", marginBottom: 16 }}>
                Get notified when new positions are posted at <strong>{settings?.company_name}</strong>.
              </p>

              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#444", display: "block", marginBottom: 5 }}>
                  Email address
                </label>
                {loggedInEmail ? (
                  <div style={{
                    display: "flex", alignItems: "center", gap: 8,
                    background: "#f6ffed", border: "1px solid #b7eb8f",
                    borderRadius: 8, padding: "8px 12px",
                  }}>
                    <i className="ti ti-circle-check" style={{ color: "#52c41a", fontSize: 15 }} />
                    <span style={{ fontSize: 13, color: "#333", flex: 1 }}>{loggedInEmail}</span>
                    <span style={{ fontSize: 11, color: "#52c41a", fontWeight: 600 }}>Logged in</span>
                  </div>
                ) : (
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Enter your email address"
                    value={alertEmail}
                    onChange={(e) => setAlertEmail(e.target.value)}
                    style={{ borderRadius: 8, fontSize: 13 }}
                  />
                )}
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#444", display: "block", marginBottom: 8 }}>
                  Alert frequency
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  {(["daily", "weekly"] as const).map((freq) => (
                    <button
                      key={freq}
                      onClick={() => setAlertFrequency(freq)}
                      style={{
                        flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 13,
                        fontWeight: alertFrequency === freq ? 600 : 400,
                        border: `1.5px solid ${alertFrequency === freq ? primaryColor : "#e2e8f0"}`,
                        background: alertFrequency === freq ? hexAlpha(primaryColor, 0.08) : "#fff",
                        color: alertFrequency === freq ? primaryColor : "#888",
                        cursor: "pointer", transition: "all 0.15s",
                      }}
                    >
                      {freq === "daily" ? "📅 Daily" : "📆 Weekly"}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: 11, color: "#aaa", marginTop: 6, marginBottom: 0 }}>
                  {alertFrequency === "daily"
                    ? "We'll send you a digest every morning with new openings."
                    : "We'll send you a weekly roundup every Monday morning."}
                </p>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer style={{ borderTop: "1px solid #f0f0f0", gap: 8 }}>
          <button
            className="btn btn-light"
            style={{ borderRadius: 8, fontSize: 13 }}
            onClick={() => setAlertModal(false)}
          >
            {alertSubscribed ? "Close" : "Cancel"}
          </button>

          
          <button
            disabled={alertLoading || alertStatusLoading}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "8px 20px", borderRadius: 8, border: "none",
              background: primaryColor, color: "#fff",
              fontSize: 13, fontWeight: 600,
              cursor: (alertLoading || alertStatusLoading) ? "not-allowed" : "pointer",
              opacity: (alertLoading || alertStatusLoading) ? 0.75 : 1,
            }}
            onClick={handleAlertSubscribe}
          >
            {alertLoading
              ? <><span className="spinner-border spinner-border-sm" /> Saving...</>
              : alertSubscribed
                ? <><i className="ti ti-refresh" /> Update Preference</>
                : <><i className="ti ti-check" /> Subscribe</>
            }
          </button>
        </Modal.Footer>
      </Modal>

      
      {settings?.company_id && (
        <GuestAuthModal
          show={showAuthModal}
          onHide={() => setShowAuthModal(false)}
          companyId={settings.company_id}
          companyType={settings.company_type}
          onSuccess={handleAuthSuccess}  
        />
      )}
    </div>
  );
};

export default CareerPage;